export type DiscernResultSortable = {
  discern_result_id: string;
  sequence?: number | null;
  bbox_id?: string | null;
  created_at: string;
  group_index?: number | null;
  page_group?: string | null;
};

export type DiscernGroupResolvable = {
  group_index?: number | null;
  page_group?: string | null;
};

/** Legacy/placeholder page_group labels use 1-based "group_N" (group_1 -> index 0). */
const PAGE_GROUP_INDEX_PATTERN = /^group_(\d+)$/;

/**
 * Shared group resolution for sorting and display.
 *
 * Prefer a valid integer `group_index` from the backend. Otherwise parse a
 * legacy/placeholder `page_group` label (e.g. "group_3" -> 2). Ungrouped or
 * unparseable results return null (sort last / display falls back to label).
 */
export function resolveDiscernGroupIndex(
  result: DiscernGroupResolvable
): number | null {
  if (Number.isInteger(result.group_index)) {
    return result.group_index as number;
  }

  const match = result.page_group?.match(PAGE_GROUP_INDEX_PATTERN);
  if (match) {
    return parseInt(match[1], 10) - 1;
  }

  return null;
}

/**
 * Sort key for curation navigation: model sequence, then numeric bbox_id, then end.
 */
export function resolveSortSequence(
  result: Pick<DiscernResultSortable, 'sequence' | 'bbox_id'>
): number {
  if (typeof result.sequence === 'number') {
    return result.sequence;
  }

  const { bbox_id } = result;
  if (bbox_id) {
    const parsed = Number(bbox_id);
    if (Number.isInteger(parsed)) {
      return parsed;
    }
  }

  return Number.MAX_SAFE_INTEGER;
}

/**
 * Group sort key using the same resolution rules as display. Ungrouped results
 * (null resolution) sort after all grouped results.
 */
export function resolveGroupIndexSortKey(
  result: DiscernGroupResolvable
): number {
  return resolveDiscernGroupIndex(result) ?? Number.MAX_SAFE_INTEGER;
}

export function compareDiscernResults(
  a: DiscernResultSortable,
  b: DiscernResultSortable
): number {
  const groupA = resolveGroupIndexSortKey(a);
  const groupB = resolveGroupIndexSortKey(b);
  if (groupA !== groupB) {
    return groupA - groupB;
  }

  const seqA = resolveSortSequence(a);
  const seqB = resolveSortSequence(b);
  if (seqA !== seqB) {
    return seqA - seqB;
  }

  const createdA = new Date(a.created_at).getTime();
  const createdB = new Date(b.created_at).getTime();
  if (createdA !== createdB) {
    return createdA - createdB;
  }

  return a.discern_result_id.localeCompare(b.discern_result_id);
}

/**
 * Returns results in chronological/model sequence order without mutating the input.
 */
export function sortDiscernResults<T extends DiscernResultSortable>(
  results: T[]
): T[] {
  return [...results].sort(compareDiscernResults);
}
